<i18n>
{
  "fr": {
    "checkout": {
      "cgv": "Vous devez accepter nos CGVs pour accéder au paiement"
    }
  }
}
</i18n>

<template>
  <div class="stripe-request-button">
    <div
      class="form-actions__btn form-actions__btn--stripe-request-button stripe-request-button__input"
      :title="$t('checkout.cgv')"
    ></div>
  </div>
</template>

<script>
import { loadStripe } from '@stripe/stripe-js';
import axios from 'axios';
import CheckoutPayment from '../../mixins/CheckoutPayment';
import Loader from '../../mixins/Loader';

const PAYMENT_METHODS = ['googlePay', 'applePay'];
const PAYMENT_ICONS = {
  googlePay: 'google',
  applePay: 'apple',
};

export default {
  name: 'StripeRequestButton',
  mixins: [CheckoutPayment, Loader],
  props: {
    publishableKey: { type: String, required: true },
    postcode: { type: String, required: true },
    paymentRequestObject: { type: Object, required: true },
    formId: { type: String, required: true },
    paymentIntentUrl: { type: String, required: true },
    methodInputSelector: { type: String, required: true },
    expectedInputValue: { type: String, required: true },
    getTokenUrl: { type: String, required: true },
    errorLogUrl: { type: String, required: true },
  },
  mounted: async function () {
    this._form = document.getElementById(this.formId);
    this._stripe = await loadStripe(this.publishableKey);
    this._checkoutPaymentMethod = document.querySelector(
      '.checkout-payment-method--stripe_request_button'
    );
    this._paymentTitle = this._checkoutPaymentMethod.querySelector(
      '.checkout-payment-method__title'
    );
    await this.createElements();
    this.addFormSubmitListener();
  },
  beforeDestroy() {
    this.destroyElements();
  },
  methods: {
    async createElements() {
      this._paymentRequest = this._stripe.paymentRequest(
        this.paymentRequestObject
      );
      const elements = this._stripe.elements();
      this._buttonElement = elements.create('paymentRequestButton', {
        paymentRequest: this._paymentRequest,
      });

      const canMakePayment = await this._paymentRequest.canMakePayment();
      if (!canMakePayment) {
        return;
      }

      this.hidePaymentMethods();
      this.updatePaymentMethodDisplay(canMakePayment);

      this._buttonElement.mount('.stripe-request-button__input');
      this._paymentRequestButton = document.querySelector(
        '.stripe-request-button__input'
      );
      this.paymentButton.parentNode.appendChild(this._paymentRequestButton);

      this._paymentRequestButton.style.display = 'none';

      this.disablePaymentButton();
      this.addEventListeners();
      this.setupPaymentRequest();
    },

    hidePaymentMethods() {
      this._checkoutPaymentMethod
        .querySelectorAll('.secure-reinsurance__payment-method')
        .forEach(element => (element.style.display = 'none'));
    },

    updatePaymentMethodDisplay(canMakePayment) {
      PAYMENT_METHODS.forEach(method => {
        if (canMakePayment[method]) {
          this._paymentTitle.innerHTML = {
            googlePay: 'Payer avec Google Pay',
            applePay: 'Payer avec Apple Pay',
          }[method];
          this._checkoutPaymentMethod.classList.remove(
            'checkout-payment-method--hide'
          );

          this._checkoutPaymentMethod
            .querySelectorAll('.secure-reinsurance__payment-method')
            .forEach(element => {
              element.style.display = element.children[0].classList.contains(
                `secure-reinsurance__payment-method-icon--${PAYMENT_ICONS[method]}`
              )
                ? null
                : 'none';
            });
        }
      });
    },

    disablePaymentButton() {
      const button = this._paymentRequestButton.children[0];
      button.style.opacity = 0.5;
      button.style.pointerEvents = 'none';
    },

    addEventListeners() {
      this.addPaymentMethodChangeListener();
      this.addTermsAndConditionsChangeListener();
    },
    setupPaymentRequest() {
      this._buttonElement.on('click', () => {
        const form = document.getElementById('payment-form');
        const data = form ? JSON.parse(form.dataset.tracking || '{}') : {};
        const checkedInput = document.querySelector(
          '.checkout-input__input:checked'
        );
        if (checkedInput) {
          globalThis.dispatchEvent(
            new CustomEvent('lgf.initiateOrderPayment', {
              detail: {
                ecommerce: { ...data, payment_type: checkedInput.value },
              },
            })
          );
        }
      });
      this._paymentRequest.on('paymentmethod', async event => {
        await this.onFormSubmit(event);
      });
    },
    addFormSubmitListener() {
      this._form.addEventListener('submit', this.onFormSubmit);
    },
    async onFormSubmit(event) {
      if (
        this.getSelectedMethod() !== 'stripe_request_button' ||
        !this.reportFormValidity()
      ) {
        if (event.complete) {
          event.complete('fail');
        }
        return;
      }
      try {
        this.setLoader();
        const clientSecret = await this.getPaymentIntent();
        const confirmResult = await this._stripe.confirmPayment({
          clientSecret,
          confirmParams: {
            payment_method: event.paymentMethod.id,
            return_url: window.location.href,
          },
          redirect: 'if_required',
        });
        await this.getToken(confirmResult);
        if (event.complete) {
          event.complete('success');
        }
        this._form.submit();
      } catch (error) {
        this.setLoader(false);
        const message =
          "Une erreur s'est produite, veuillez retenter votre paiement. Si l'erreur persiste, merci de contacter l'administrateur du site.";
        window.flashMessages = [
          {
            type: 'alert-error',
            message,
          },
        ];
        window.addToastMessages();

        Object.defineProperty(Error.prototype, 'toJSON', {
          value() {
            const alt = {};
            Object.getOwnPropertyNames(this).forEach(key => {
              alt[key] = this[key];
            });
            return alt;
          },
          configurable: true,
          writable: true,
        });
        const errorLog = error.toJSON();
        errorLog.response = error.response;

        await axios.post(this.errorLogUrl, {
          _token: this.getCsrfToken(),
          error: JSON.stringify(errorLog),
        });
        if (event.complete) {
          event.complete('fail');
        }

        if (
          error.response &&
          error.response.data &&
          error.response.data.redirect_url
        ) {
          window.location.href = error.response.data.redirect_url;
        }
      }
    },
    async getPaymentIntent() {
      const response = await axios.get(this.paymentIntentUrl);
      if (typeof response.data.clientSecret === 'undefined') {
        const error = new Error('Missing client secret');
        error.debugData = response;
        throw error;
      }
      return response.data.clientSecret;
    },
    async getToken(result) {
      if (!result || !result.paymentIntent) {
        const error = new Error('Malformed response received from Stripe');
        error.debugData = result;
        throw error;
      }
      return axios.post(this.getTokenUrl, {
        _token: this.getCsrfToken(),
        paymentMethod: result.paymentIntent.payment_method,
      });
    },
    getCsrfToken() {
      return document.querySelector('meta[name="csrf-token"]').content;
    },
    destroyElements() {
      if (this._form) {
        this._form.removeEventListener('submit', this.onFormSubmit);
      }
    },
    getSelectedMethod() {
      const selected = document.querySelector('.checkout-input__input:checked');
      return selected ? selected.value : null;
    },
    addPaymentMethodChangeListener() {
      const elements = Array.from(
        document.querySelectorAll(this.methodInputSelector)
      );

      elements.forEach(input =>
        input.addEventListener('change', event => {
          this._paymentRequestButton.style.display =
            this.expectedInputValue === event.target.value ? 'block' : 'none';

          this.paymentButton.style.display = this.hasVisibleCustomButtons()
            ? 'none'
            : 'inline-flex';
        })
      );

      const selectedElement = elements.find(element => element.checked);
      if (selectedElement) {
        this._paymentRequestButton.style.display =
          this.expectedInputValue === selectedElement.value ? 'block' : 'none';

        this.paymentButton.style.display = this.hasVisibleCustomButtons()
          ? 'none'
          : 'inline-flex';
      }
    },
    addTermsAndConditionsChangeListener() {
      const button = this._paymentRequestButton.children[0];

      if (this.termsAndConditionsInput && button) {
        this.termsAndConditionsInput.addEventListener('change', event => {
          button.style.opacity = event.target.checked ? 1 : 0.5;
          button.style.pointerEvents = event.target.checked ? 'auto' : 'none';
        });
      }
    },
  },
};
</script>
